import { AnyAction } from 'redux';
import {
	RESET_AUTO_STASH_FLOW,
	UPDATE_AUTO_STASH_FLOW,
	type AutoStashUpdateAction,
} from 'stash/actions/autoStashUpdate';
import {
	type AutoStashAutomationFrequency,
	AutoStashAutomationStatus,
	AutoStashFundingSourceItem,
	AUTOMATION_FREQUENCY,
} from 'stash/types/apis/autostash';
import type { Amount } from 'stash/types/models/Amount';

export type AutoStashCurrentAutomation = {
	id: string;
	amount: Amount;
	frequency: AutoStashAutomationFrequency;
	nextDate: string;
	status: AutoStashAutomationStatus;
};

/**
 * Coarse Auto Stash state for the destination account in scope. Collapses the
 * `AUTOMATION_STATUS_*` enum into the three buckets the UI actually branches
 * on. Hydrated from `currentAutomation.status` during the same dispatch that
 * seeds `currentAutomation` so consumers can read a single source of truth
 * out of Redux instead of recomputing.
 */
export type AutoStashVariant = 'active' | 'paused' | 'off';

/**
 * Latest IRA match-reward result for the selected amount + destination path
 * type. Both fields are `null` when the API has not yet returned for the
 * current params, when the params are invalid (no amount / no destination
 * type), or when the request errored. Consumers should treat a `null`
 * `amount` as "no badge to render" rather than a loading state.
 */
export type AutoStashMatchReward = {
	amount: Amount | null;
	percentage: string | null;
};

/**
 * Holds in-memory state for the Auto Stash update flow.
 *
 * Tracks the selections the user has made in the sheets (frequency + amount)
 * plus the questions-flow plumbing:
 *
 * - `selectedFrequency` drives the amount-suggestion math in
 *   `useAutoStashLogic.getAutoStashAmounts` (periods-per-year scales the
 *   annual income to a single-period dollar value) and the highlighted
 *   option in `FrequencySheet`. Defaults to MONTHLY.
 * - `selectedAmount` is the dollar amount the user picked in the amount
 *   sheet, used to highlight the active option on re-open. `null` until the
 *   user makes a choice. Note: when `selectedFrequency` changes, the saved
 *   amount no longer matches the new period's options, so the highlight is
 *   intentionally lost — the user needs to re-pick.
 * - `hasVisitedQuestionsFlow` gates the "open Amount sheet vs redirect to
 *   questions" decision in `useAutoStashSheets`.
 * - `returnUrl` is the URL the user was on when they entered the questions
 *   flow. The skip CTAs on `auto-stashV2/screens/*Container` use it to
 *   send the user back where they started, and `AutoStashTransitionScreen`
 *   uses it to bounce out when there are no remaining questions to ask.
 */
export type AutoStashUpdateState = {
	/**
	 * Frequency selected in the FrequencySheet. Drives both the highlighted
	 * option on re-open and the periods-per-year used to compute amount
	 * suggestions in `useAutoStashLogic`. Defaults to MONTHLY.
	 */
	selectedFrequency: AutoStashAutomationFrequency | null;
	/**
	 * Dollar amount selected in the AmountSheet. `null` until the user makes
	 * a choice. Used to highlight the active option on re-open.
	 */
	selectedAmount: number | null;
	/**
	 * Date of the next scheduled transfer for the destination account's
	 * automation. For paused automations this is the resume date the user
	 * picked (or hydrated from the BE's `nextExecutionDate`). `null` until
	 * either the BE row is loaded or the user picks a resume date.
	 */
	selectedNextTransferDate: string | null;
	/**
	 * Funding source selected in the FundingSourceSheet, or hydrated from the
	 * existing automation on first load. `null` until either happens. Holds
	 * the full source object (`id`, `pathType`, `name`, optional
	 * `availableToTransfer`).
	 */
	selectedFundingSource: AutoStashFundingSourceItem | null;
	/**
	 * `true` once the user has been routed into the questions flow from the
	 * Auto Stash update entry points. Used to avoid re-redirecting users who
	 * already chose to skip the questions and should instead see the amount
	 * sheet (custom-only) on subsequent taps.
	 */
	hasVisitedQuestionsFlow: boolean;
	/**
	 * Absolute URL (pathname + search) the user was on when they were routed
	 * into the questions flow. `null` until the flow is entered. Consumed by
	 * skip CTAs on the question screens and by the transition screen's
	 * "nothing-to-do" fallback to bounce the user back where they came from.
	 */
	returnUrl: string | null;
	/**
	 * One-shot signal: set to `true` at the moment we redirect the user
	 * into the questions flow from the Amount entry point, consumed (and
	 * immediately cleared) when they land back on the entry-point screen so
	 * the amount sheet auto-opens without requiring a second tap. Modeled
	 * separately from `hasVisitedQuestionsFlow` because the two have
	 * different lifetimes — `hasVisitedQuestionsFlow` is sticky and gates
	 * future redirects, while this is consumed on first read so re-mounts
	 * of the entry-point screen (e.g. clicking "Edit" from the conversion
	 * Summary) don't re-pop the sheet. Only the `useAutoStashSheets`
	 * consumer that actually renders `sheets` should observe this — see
	 * the hook's `autoOpenOnReturn` option.
	 */
	shouldOpenAmountSheetOnReturn: boolean;
	/**
	 * Snapshot of the user's currently-saved automation for the destination
	 * account in scope. Hydrated on hook mount from the `/schedules/scheduled`
	 * response. `null` until the matching row is loaded (or when no scheduled
	 * automation exists for the account yet). See
	 * `AutoStashCurrentAutomation` for field semantics.
	 */
	currentAutomation: AutoStashCurrentAutomation | null;
	/**
	 * Coarse derived view of `currentAutomation.status` that the UI branches on
	 * (`active` | `paused` | `off`). Defaults to `'off'` so accounts with no
	 * scheduled automation read correctly without any hydration. Kept in sync
	 * with `currentAutomation` by `useAutoStashLogic`'s hydration effect.
	 */
	autoStashVariant: AutoStashVariant;
	/**
	 * The Auto Stash variant the user has selected for the *new* automation in
	 * the conversion flow (i.e. what they want their post-conversion AS state
	 * to be). Distinct from `autoStashVariant`, which mirrors the existing BE
	 * automation. Defaults to `null` (no choice yet) so the investing-plan
	 * card on the Summary screen renders nothing until the user picks an
	 * option in the adjust-AS sheet; `'off'` is also treated as "render
	 * nothing" by `AutoStashConfirmationSection`.
	 */
	newAutoStashSelection: AutoStashVariant | null;
	/**
	 * Latest IRA match-reward result for the current `selectedAmount` and the
	 * destination account's path type. Updated by `useAutoStashLogic`'s
	 * debounced fetch on amount changes; reset to `{ amount: null, percentage:
	 * null }` whenever the amount is invalid or the request errors.
	 */
	matchReward: AutoStashMatchReward;
};

const initialState: AutoStashUpdateState = {
	selectedFrequency: AUTOMATION_FREQUENCY.MONTHLY as AutoStashAutomationFrequency,
	selectedAmount: 250,
	selectedNextTransferDate: null,
	selectedFundingSource: null,
	hasVisitedQuestionsFlow: false,
	returnUrl: null,
	shouldOpenAmountSheetOnReturn: false,
	currentAutomation: null,
	autoStashVariant: 'off',
	newAutoStashSelection: null,
	matchReward: { amount: null, percentage: null },
};

const autoStashUpdate = (
	state: AutoStashUpdateState = initialState,
	action: AnyAction,
): AutoStashUpdateState => {
	switch (action.type) {
		case UPDATE_AUTO_STASH_FLOW: {
			const { payload } = action as AutoStashUpdateAction & {
				type: typeof UPDATE_AUTO_STASH_FLOW;
			};
			return { ...state, ...payload };
		}
		case RESET_AUTO_STASH_FLOW: {
			return initialState;
		}
		default:
			return state;
	}
};

export default autoStashUpdate;
